import {
    Axis,
    ConfigurableItemMapper,
    CrosspointDataProviderConfiguration,
    DataProviderConfigurations,
    InMemoryCrosspointDataService,
    Viewport
} from "@o2d/datasource";
import {DateTime, Interval} from "luxon";
import {axisEntityMetadata} from "../../services/backend-axis-data.service";
import {RestApiCrosspointDataService} from "../../o2d-timeline/crosspoint/rest-api-crosspoint-data.service";
import {dateTimeAxisBuilder, entityAxisBuilder} from "../../connectivity/viewport-query.types";
import {Issue} from "../../domain/entities/issue.entity";
import type {LayoutTemplate} from "@o2d/grid-layout";
import type {HeaderItem, DateTimeHeader} from "./types/header-item.type";
import type {TimelineViewConfig} from "./timeline-view.config";
import {deriveXAxisSections} from "./timeline-layout";
import {CALENDAR_PROJECT_UID} from "../../constants";

/**
 * Static control-cell provider: yields exactly one fixed item so
 * grid-area-control1's cellRenderer (see
 * createColorFilterControlAreaConfiguration() in timeline-area.factory.ts)
 * fires once, with no real data/calc. Pulled out into its own factory
 * (2026-08-28) alongside that area config — same rationale: entirely
 * self-contained (fixed A×C3 axis ids, fixed synthetic item), so every view
 * whose own axis config also registers axisId 'A' over the template's fixed
 * column sections and axisId 'C3' over row-section-3 can reuse this same
 * provider entry instead of re-implementing it (currently: the standard
 * timeline views below, and resource-planning-data-providers.factory.ts).
 *
 * Its span stays [0, 0] regardless of how many fixed columns the template has:
 * this provider owns exactly one area (grid-area-control1, on column-section-0),
 * and an area's index is counted per provider, not across the grid.
 */
export const createColorFilterControlDataProvider = (): CrosspointDataProviderConfiguration<unknown, unknown, unknown> => ({
    axisIdX: 'A',
    axisIdY: 'C3',
    itemDataService: {
        class: InMemoryCrosspointDataService,
        generator: (): { id: string }[] => [{ id: 'grid-area-control1-static' }]
    },
    itemMapper: new ConfigurableItemMapper<number, number, { id: string }>({
        x: () => [0, 0],
        y: () => [0, 0],
        idPath: 'id'
    })
});

/**
 * Creates data provider configurations for a timeline view.
 * Combines entity-specific header/content providers with shared DateTime header providers.
 *
 * @param config - Entity-specific configuration
 * @param layoutTemplate - The view's layout template. Only the row-header provider (A×D)
 *                         reads it, to span its item across however many fixed columns the
 *                         template declares. Required rather than defaulted: see the note
 *                         on createTimelineAxisConfigurations.
 */
export const createTimelineDataProviders = (
    config: TimelineViewConfig,
    layoutTemplate: LayoutTemplate
): DataProviderConfigurations<unknown> => {

// Same derivation the axis factory uses, so the item's span and the axis it spans over can
// never disagree. `spanX` is an inclusive range of axis VALUES, not a column count, hence
// first-to-last rather than a length.
const {fixedValues} = deriveXAxisSections(layoutTemplate);
const fixedColumnSpan = (): [number, number] => [fixedValues[0], fixedValues[fixedValues.length - 1]];

return [
    // Header-DataProvider: Row labels from axis metadata
    {
        axisIdX: 'A',
        axisIdY: 'D',
        itemDataService: {
            class: InMemoryCrosspointDataService,
            generator: (_viewport: Viewport, _xAxis: Axis<number>, yAxis: Axis<string>): HeaderItem[] => {
                const items: HeaderItem[] = [];
                const [minY, maxY] = yAxis.loadedBoundaries;

                for (let yi = minY; yi <= maxY; yi++) {
                    const yValue = yAxis.getValue(yi);
                    if (!yValue) continue;

                    const meta = axisEntityMetadata.get(yValue);
                    items.push({
                        uid: yValue,
                        title: meta?.title ?? yValue,
                        color: meta?.color ?? null,
                        entities: [{uid: yValue}],
                        interval: Interval.fromDateTimes(DateTime.now(), DateTime.now())
                    });
                }
                return items;
            }
        },
        itemMapper: new ConfigurableItemMapper<number, string, HeaderItem>({
            // Spans every fixed row-header column, however many the template declares.
            x: fixedColumnSpan,
            y: 'entities[*].uid',
            idPath: 'uid'
        })
    },
    createColorFilterControlDataProvider(),
    // Calendar Header-DataProvider: Row label for calendar project row (A × C4)
    // Mirrors the A×D header pattern — reads axisEntityMetadata populated by B×C4 provider.
    // There is exactly one entry per axis pair on purpose: the lookup that turns these
    // configurations into providers takes the FIRST match (findCrosspointDataProviderConfiguration
    // in @o2d/timeline), so a second A×C4 entry would be registered nowhere and silently do
    // nothing. One such duplicate existed here and was removed.
    {
        axisIdX: 'A',
        axisIdY: 'C4',
        itemDataService: {
            class: InMemoryCrosspointDataService,
            generator: (_viewport: Viewport, _xAxis: Axis<number>, yAxis: Axis<string>): HeaderItem[] => {
                const items: HeaderItem[] = [];
                const [minY, maxY] = yAxis.loadedBoundaries;

                for (let yi = minY; yi <= maxY; yi++) {
                    const yValue = yAxis.getValue(yi);
                    if (!yValue) continue;

                    const meta = axisEntityMetadata.get(yValue);
                    items.push({
                        uid: yValue,
                        title: meta?.title ?? yValue,
                        color: meta?.color ?? null,
                        entities: [{uid: yValue}],
                        interval: Interval.fromDateTimes(DateTime.now(), DateTime.now())
                    });
                }
                return items;
            }
        },
        itemMapper: new ConfigurableItemMapper<number, string, HeaderItem>({
            // Stays [0, 2] even under t4, and that is not an oversight. The calendar row
            // has no area on the extra column, so this provider still owns exactly three
            // areas — and an area's index is counted per provider, in grid order, so the
            // skipped column shifts nothing here.
            x: () => [0, 2],
            y: 'entities[*].uid',
            idPath: 'uid'
        })
    },
    // Content-DataProvider: Issues from backend via viewport-query
    {
        axisIdX: 'B',
        axisIdY: 'D',
        itemDataService: {
            class: RestApiCrosspointDataService,
            xAxisConfig: dateTimeAxisBuilder,
            yAxisConfig: entityAxisBuilder({ entityType: config.entityType, sort: config.sort, queryParams: config.queryParams }),
            includes: config.includes
        },
        itemMapper: new ConfigurableItemMapper<string, string, Issue>({
            x: (item: Issue) => [
                item.interval.start!.toISODate()!,
                item.interval.end!.minus({days: 1}).toISODate()!
            ],
            y: config.yItemPath,
            idPath: 'uid'
        })
    },
    // DateTime Header: Month/Year (row-section-0)
    {
        axisIdX: 'B',
        axisIdY: 'C0',
        itemDataService: {
            class: InMemoryCrosspointDataService,
            generator: (_viewport: Viewport, xAxis: Axis<string>, _yAxis: Axis<number>): DateTimeHeader[] => {
                const [minX, maxX] = xAxis.loadedBoundaries;
                const startX = xAxis.getValue(minX);
                const endX = xAxis.getValue(maxX);
                if (!startX || !endX) throw new Error('Invalid date range');

                const startDate = DateTime.fromISO(startX).startOf('day');
                const endDate = DateTime.fromISO(endX).startOf('day');

                // Split at calendar month boundaries, but clip ranges to loaded axis data
                return Interval
                    .fromDateTimes(startDate.startOf('month'), endDate.plus({months: 1}).startOf('month'))
                    .splitBy({months: 1})
                    .map(month => {
                        const rangeStart = DateTime.max(month.start!, startDate);
                        const rangeEnd = DateTime.min(month.end!.minus({day: 1}), endDate);
                        return {
                            range: [rangeStart.toISODate()!, rangeEnd.toISODate()!] as [string, string],
                            id: `xheader_${month.start!.toFormat('yyyy-LL')}`
                        };
                    });
            }
        },
        itemMapper: new ConfigurableItemMapper<string, number, DateTimeHeader>({
            x: {start: 'range.0', end: 'range.1'},
            y: () => [0, 0],
            idPath: 'id'
        })
    },
    // DateTime Header: Calendar Week (row-section-1)
    {
        axisIdX: 'B',
        axisIdY: 'C1',
        itemDataService: {
            class: InMemoryCrosspointDataService,
            generator: (_viewport: Viewport, xAxis: Axis<string>, _yAxis: Axis<number>): DateTimeHeader[] => {
                const [minX, maxX] = xAxis.loadedBoundaries;
                const startX = xAxis.getValue(minX);
                const endX = xAxis.getValue(maxX);
                if (!startX || !endX) throw new Error('Invalid date range');

                let startDateX = DateTime.fromISO(startX).startOf('day');
                const endDateX = DateTime.fromISO(endX).startOf('day');
                const weeks: DateTimeHeader[] = [];

                if (startDateX.localWeekday !== 1) {
                    const nextBeginOfWeek = startDateX.plus({days: (8 - startDateX.localWeekday)});
                    weeks.push({
                        range: [startDateX.toISODate()!, nextBeginOfWeek.minus({days: 1}).toISODate()!],
                        id: `xheader_${startDateX.year}-${startDateX.localWeekNumber}`
                    });
                    startDateX = nextBeginOfWeek;
                }

                const fullWeeks = Interval
                    .fromDateTimes(startDateX, endDateX)
                    .splitBy({week: 1})
                    .map<DateTimeHeader>(week => ({
                        range: [week.start!.toISODate()!, week.end!.minus({days: 1}).toISODate()!],
                        id: `xheader_${week.start!.year}-${week.start!.localWeekNumber}`
                    }));

                return [...weeks, ...fullWeeks];
            }
        },
        itemMapper: new ConfigurableItemMapper<string, number, DateTimeHeader>({
            x: {start: 'range.0', end: 'range.1'},
            y: () => [0, 0],
            idPath: 'id'
        })
    },
    // DateTime Header: Weekday (row-section-2)
    {
        axisIdX: 'B',
        axisIdY: 'C2',
        itemDataService: {
            class: InMemoryCrosspointDataService,
            generator: (_viewport: Viewport, xAxis: Axis<string>, _yAxis: Axis<number>): DateTimeHeader[] => {
                const [minX, maxX] = xAxis.loadedBoundaries;
                const startX = xAxis.getValue(minX);
                const endX = xAxis.getValue(maxX);
                if (!startX || !endX) throw new Error('Invalid date range');

                return Interval
                    .fromDateTimes(DateTime.fromISO(startX).startOf('day'), DateTime.fromISO(endX).startOf('day'))
                    .splitBy({day: 1})
                    .map(day => ({
                        range: [day.start!.toISODate()!, day.start!.toISODate()!] as [string, string],
                        id: `xheader_weekday_${day.start!.toISODate()}`
                    }));
            }
        },
        itemMapper: new ConfigurableItemMapper<string, number, DateTimeHeader>({
            x: {start: 'range.0', end: 'range.1'},
            y: () => [0, 0],
            idPath: 'id'
        })
    },
    // DateTime Header: Day of Month (row-section-3)
    {
        axisIdX: 'B',
        axisIdY: 'C3',
        itemDataService: {
            class: InMemoryCrosspointDataService,
            generator: (_viewport: Viewport, xAxis: Axis<string>, _yAxis: Axis<number>): DateTimeHeader[] => {
                const [minX, maxX] = xAxis.loadedBoundaries;
                const startX = xAxis.getValue(minX);
                const endX = xAxis.getValue(maxX);
                if (!startX || !endX) throw new Error('Invalid date range');

                return Interval
                    .fromDateTimes(DateTime.fromISO(startX).startOf('day'), DateTime.fromISO(endX).startOf('day'))
                    .splitBy({day: 1})
                    .map(day => ({
                        range: [day.start!.toISODate()!, day.start!.toISODate()!] as [string, string],
                        id: `xheader_day_${day.start!.toISODate()}`
                    }));
            }
        },
        itemMapper: new ConfigurableItemMapper<string, number, DateTimeHeader>({
            x: {start: 'range.0', end: 'range.1'},
            y: () => [0, 0],
            idPath: 'id'
        })
    },
    // Calendar Content-DataProvider: Issues from calendar project only (B × C4)
    {
        axisIdX: 'B',
        axisIdY: 'C4',
        itemDataService: {
            class: RestApiCrosspointDataService,
            xAxisConfig: dateTimeAxisBuilder,
            yAxisConfig: entityAxisBuilder({
                entityType: 'Project',
                sort: { strategy: 'property', path: 'title', direction: 'ASC' },
                queryParams: [{ key: 'uid', comparator: 'eq', value: CALENDAR_PROJECT_UID }]
            }),
            includes: ['project', 'type', 'status', 'employees', 'keywords', 'creator']
        },
        itemMapper: new ConfigurableItemMapper<string, string, Issue>({
            x: (item: Issue) => [
                item.interval.start!.toISODate()!,
                item.interval.end!.minus({days: 1}).toISODate()!
            ],
            y: 'project.uid',
            idPath: 'uid'
        })
    }
];
};
